import type { InstrumentModelProfile, VariantModelProfile } from "./types";

const warning =
  "Browser-safe physical/spectral approximation. It is not an authentic recreation and must remain marked as source-review pending.";

function variant(
  variantId: string,
  values: Partial<Omit<VariantModelProfile, "variantId" | "metadataTags">> & { metadataTags?: string[] }
): VariantModelProfile {
  return {
    variantId,
    brightness: values.brightness ?? 0.55,
    decay: values.decay ?? 0.55,
    attack: values.attack ?? 0.45,
    sustain: values.sustain ?? 0.5,
    bodyGain: values.bodyGain ?? 0.55,
    noiseGain: values.noiseGain ?? 0.35,
    modulationDepth: values.modulationDepth ?? 0.45,
    metadataTags: values.metadataTags ?? []
  };
}

export const instrumentModelProfiles: Record<string, InstrumentModelProfile> = {
  gayageum: {
    instrumentId: "gayageum",
    family: "plucked_string_waveguide",
    defaultVariantId: "sanjo12",
    layers: {
      exciter: "Karplus-Strong pluck plus 2-8ms broadband click and bridge scrape.",
      resonator: "Odong wood body modal bank around 90-160Hz, 180-280Hz, 420-650Hz, and 1.2-2.5kHz.",
      articulation: "Nonghyeon bends pitch upward asymmetrically while also modulating brightness.",
      residual: "Velocity-linked bridge scrape and short tactile pluck noise."
    },
    approximationWarning: warning,
    variantProfiles: {
      sanjo12: variant("sanjo12", {
        brightness: 0.68,
        decay: 0.48,
        attack: 0.82,
        sustain: 0.42,
        bodyGain: 0.64,
        noiseGain: 0.52,
        modulationDepth: 0.84,
        metadataTags: ["fast_attack", "strong_nonghyeon"]
      }),
      pungryuBeomgeum12: variant("pungryuBeomgeum12", {
        brightness: 0.38,
        decay: 0.72,
        attack: 0.34,
        sustain: 0.62,
        bodyGain: 0.78,
        noiseGain: 0.25,
        modulationDepth: 0.34,
        metadataTags: ["rounded_attack", "restrained_nonghyeon"]
      }),
      modern25: variant("modern25", {
        brightness: 0.74,
        decay: 0.66,
        attack: 0.58,
        sustain: 0.7,
        bodyGain: 0.5,
        noiseGain: 0.28,
        modulationDepth: 0.28,
        metadataTags: ["modern_strings", "stable_pitch", "expanded_range"]
      }),
      modern21: variant("modern21", {
        brightness: 0.6,
        decay: 0.6,
        attack: 0.54,
        sustain: 0.62,
        bodyGain: 0.52,
        noiseGain: 0.3,
        modulationDepth: 0.32,
        metadataTags: ["archive_or_regional", "modern_strings"]
      })
    }
  },
  erhu: {
    instrumentId: "erhu",
    family: "bowed_string_friction",
    defaultVariantId: "erhu_hexagonal",
    layers: {
      exciter: "Sustained bowed oscillator through nonlinear friction waveshaper.",
      resonator: "Python skin membrane and small soundbox formant bank.",
      articulation: "Rou xian moves pitch, amplitude, and brightness together.",
      residual: "Bow scrape bandpass noise with pressure and speed jitter."
    },
    approximationWarning: warning,
    variantProfiles: {
      erhu_hexagonal: variant("erhu_hexagonal", {
        brightness: 0.58,
        decay: 0.62,
        attack: 0.5,
        sustain: 0.82,
        bodyGain: 0.74,
        noiseGain: 0.42,
        modulationDepth: 0.72,
        metadataTags: ["python_skin", "hexagonal", "nasal_midrange"]
      }),
      erhu_octagonal: variant("erhu_octagonal", {
        brightness: 0.54,
        decay: 0.66,
        attack: 0.48,
        sustain: 0.84,
        bodyGain: 0.7,
        noiseGain: 0.38,
        modulationDepth: 0.66,
        metadataTags: ["python_skin", "octagonal", "broader_body"]
      }),
      erhu_rare_circular: variant("erhu_rare_circular", {
        brightness: 0.42,
        decay: 0.7,
        attack: 0.42,
        sustain: 0.8,
        bodyGain: 0.66,
        noiseGain: 0.32,
        modulationDepth: 0.56,
        metadataTags: ["python_skin", "rare_circular", "soft_nasal"]
      })
    }
  },
  oud: {
    instrumentId: "oud",
    family: "plucked_string_waveguide",
    defaultVariantId: "arabic_oud",
    layers: {
      exciter: "Two coupled Karplus-Strong course strings with risha click and scratch.",
      resonator: "Shared bowl body resonator with low-mid thump.",
      articulation: "Fretless micro bend uses smooth course pitch trajectories.",
      residual: "1-5ms risha click, 2-5kHz scratch, and course beating."
    },
    approximationWarning: warning,
    variantProfiles: {
      arabic_oud: variant("arabic_oud", {
        brightness: 0.44,
        decay: 0.58,
        attack: 0.58,
        sustain: 0.46,
        bodyGain: 0.82,
        noiseGain: 0.42,
        modulationDepth: 0.46,
        metadataTags: ["warm_bowl", "maqam_context"]
      }),
      turkish_oud: variant("turkish_oud", {
        brightness: 0.72,
        decay: 0.46,
        attack: 0.78,
        sustain: 0.38,
        bodyGain: 0.58,
        noiseGain: 0.48,
        modulationDepth: 0.52,
        metadataTags: ["bright_attack", "makam_context"]
      }),
      iraqi_oud: variant("iraqi_oud", {
        brightness: 0.58,
        decay: 0.64,
        attack: 0.62,
        sustain: 0.5,
        bodyGain: 0.76,
        noiseGain: 0.4,
        modulationDepth: 0.5,
        metadataTags: ["iraqi_lineage", "optional_floating_bridge", "body_response_not_bridge_only"]
      })
    }
  },
  sitar: {
    instrumentId: "sitar",
    family: "plucked_string_waveguide",
    defaultVariantId: "kharaj_pancham",
    layers: {
      exciter: "Mizraab-triggered waveguide with amplitude-dependent jawari branch.",
      resonator: "Main string plus 11-13 low-gain sympathetic resonators.",
      articulation: "Meend increases pitch, jawari intensity, and brightness together.",
      residual: "Jawari rasp and chikari string layer remain after attack."
    },
    approximationWarning: warning,
    variantProfiles: {
      kharaj_pancham: variant("kharaj_pancham", {
        brightness: 0.72,
        decay: 0.78,
        attack: 0.72,
        sustain: 0.74,
        bodyGain: 0.72,
        noiseGain: 0.58,
        modulationDepth: 0.72,
        metadataTags: ["jawari", "taraf", "kharaj_bass"]
      }),
      gandhar_pancham: variant("gandhar_pancham", {
        brightness: 0.68,
        decay: 0.72,
        attack: 0.7,
        sustain: 0.7,
        bodyGain: 0.68,
        noiseGain: 0.54,
        modulationDepth: 0.66,
        metadataTags: ["jawari", "taraf", "gandhar_setup"]
      }),
      surbahar: variant("surbahar", {
        brightness: 0.44,
        decay: 0.9,
        attack: 0.38,
        sustain: 0.86,
        bodyGain: 0.88,
        noiseGain: 0.4,
        modulationDepth: 0.52,
        metadataTags: ["lower_range", "heavy_resonance", "slow_attack"]
      })
    }
  },
  daegeum: {
    instrumentId: "daegeum",
    family: "edge_tone_bore",
    defaultVariantId: "jeongak_daegeum",
    layers: {
      exciter: "Edge-tone flute oscillator mixed with tilted breath turbulence.",
      resonator: "Bamboo bore modal bank and cheong membrane branch.",
      articulation: "Yoseong modulates pitch, breath pressure, and filter brightness.",
      residual: "Threshold cheong rattle above pressure/exposure threshold."
    },
    approximationWarning: warning,
    variantProfiles: {
      jeongak_daegeum: variant("jeongak_daegeum", {
        brightness: 0.42,
        decay: 0.72,
        attack: 0.38,
        sustain: 0.82,
        bodyGain: 0.78,
        noiseGain: 0.24,
        modulationDepth: 0.34,
        metadataTags: ["stable_pitch", "low_breath", "restrained_yoseong"]
      }),
      sanjo_daegeum: variant("sanjo_daegeum", {
        brightness: 0.68,
        decay: 0.58,
        attack: 0.72,
        sustain: 0.76,
        bodyGain: 0.7,
        noiseGain: 0.48,
        modulationDepth: 0.72,
        metadataTags: ["strong_attack", "cheong_response", "active_yoseong"]
      })
    }
  },
  ney: {
    instrumentId: "ney",
    family: "edge_tone_bore",
    defaultVariantId: "arabic_ney",
    layers: {
      exciter: "End-blown edge-tone oscillator with breath turbulence.",
      resonator: "Tube resonator bank and embouchure-dependent filtering.",
      articulation: "Microbend changes pitch, breath ratio, and edge brightness.",
      residual: "Breath/edge roughness, especially in Persian interdental profile."
    },
    approximationWarning: warning,
    variantProfiles: {
      arabic_ney: variant("arabic_ney", {
        brightness: 0.42,
        decay: 0.68,
        attack: 0.46,
        sustain: 0.82,
        bodyGain: 0.66,
        noiseGain: 0.58,
        modulationDepth: 0.62,
        metadataTags: ["breathy", "maqam_bend", "low_edge_brightness"]
      }),
      turkish_ney: variant("turkish_ney", {
        brightness: 0.36,
        decay: 0.72,
        attack: 0.34,
        sustain: 0.86,
        bodyGain: 0.72,
        noiseGain: 0.44,
        modulationDepth: 0.42,
        metadataTags: ["soft_attack", "controlled_embouchure", "stable_pitch"]
      }),
      persian_ney: variant("persian_ney", {
        brightness: 0.74,
        decay: 0.62,
        attack: 0.58,
        sustain: 0.78,
        bodyGain: 0.62,
        noiseGain: 0.64,
        modulationDepth: 0.7,
        metadataTags: ["interdental_embouchure", "high_harmonics", "edge_roughness"]
      })
    }
  },
  janggu: {
    instrumentId: "janggu",
    family: "membrane_modal",
    defaultVariantId: "pungmul_janggu",
    layers: {
      exciter: "Hand, stick, rim, blocked, and both-open transient vectors.",
      resonator: "Separate low gungpyeon and high chaepyeon modal membrane banks.",
      articulation: "Stroke id remains separate from gueum label.",
      residual: "Stick click, rim slap, and head noise by stroke zone."
    },
    approximationWarning: warning,
    variantProfiles: {
      pungmul_janggu: variant("pungmul_janggu", {
        brightness: 0.72,
        decay: 0.48,
        attack: 0.84,
        sustain: 0.24,
        bodyGain: 0.72,
        noiseGain: 0.62,
        modulationDepth: 0.36,
        metadataTags: ["wide_dynamic_range", "transient_forward"]
      }),
      court_janggu: variant("court_janggu", {
        brightness: 0.46,
        decay: 0.34,
        attack: 0.44,
        sustain: 0.18,
        bodyGain: 0.54,
        noiseGain: 0.32,
        modulationDepth: 0.22,
        metadataTags: ["restrained_decay", "stable_pitch_center"]
      })
    }
  },
  tabla: {
    instrumentId: "tabla",
    family: "membrane_modal",
    defaultVariantId: "hindustani_tabla_pair",
    layers: {
      exciter: "Bol-specific finger, edge, syahi, and palm excitation vectors.",
      resonator: "Separate quasi-harmonic dayan and pressure-gliding bayan modal banks.",
      articulation: "Bayan pressure changes modal frequency, decay, and spectral centroid.",
      residual: "Skin clicks and syahi-colored attack noise."
    },
    approximationWarning: warning,
    variantProfiles: {
      hindustani_tabla_pair: variant("hindustani_tabla_pair", {
        brightness: 0.64,
        decay: 0.54,
        attack: 0.68,
        sustain: 0.36,
        bodyGain: 0.7,
        noiseGain: 0.48,
        modulationDepth: 0.72,
        metadataTags: ["dayan_syahi", "bayan_pressure"]
      }),
      concert_tabla_pair: variant("concert_tabla_pair", {
        brightness: 0.58,
        decay: 0.62,
        attack: 0.58,
        sustain: 0.42,
        bodyGain: 0.72,
        noiseGain: 0.4,
        modulationDepth: 0.62,
        metadataTags: ["stage_tuning", "same_syahi_logic"]
      })
    }
  },
  mbira: {
    instrumentId: "mbira",
    family: "metal_tine_modal",
    defaultVariantId: "mbira_dzavadzimu",
    layers: {
      exciter: "Short hard tine click.",
      resonator: "Cantilever metal tine modal bank around 1f, 5f, and 14f plus deze/calabash body.",
      articulation: "Manual role metadata remains separate from linear keyboard order.",
      residual: "Delayed gated buzz/rattle that opens after the tine onset."
    },
    approximationWarning: warning,
    variantProfiles: {
      mbira_dzavadzimu: variant("mbira_dzavadzimu", {
        brightness: 0.72,
        decay: 0.82,
        attack: 0.76,
        sustain: 0.68,
        bodyGain: 0.7,
        noiseGain: 0.58,
        modulationDepth: 0.5,
        metadataTags: ["22_28_tines", "deze_buzz", "manual_context"]
      })
    }
  },
  kora: {
    instrumentId: "kora",
    family: "plucked_string_waveguide",
    defaultVariantId: "traditional_21_kora",
    layers: {
      exciter: "Rank-aware plucked string delay line with thumb/index timing color.",
      resonator: "Calabash 120-300Hz boom and 600-900Hz skin resonance.",
      articulation: "Left/right ranks are separated in stereo field.",
      residual: "Very light optional rattle kept separate from cultural description."
    },
    approximationWarning: warning,
    variantProfiles: {
      traditional_21_kora: variant("traditional_21_kora", {
        brightness: 0.54,
        decay: 0.74,
        attack: 0.42,
        sustain: 0.72,
        bodyGain: 0.82,
        noiseGain: 0.36,
        modulationDepth: 0.5,
        metadataTags: ["two_ranks", "jali_griot_context", "soft_attack"]
      }),
      keur_moussa_kora: variant("keur_moussa_kora", {
        brightness: 0.5,
        decay: 0.7,
        attack: 0.48,
        sustain: 0.68,
        bodyGain: 0.72,
        noiseGain: 0.26,
        modulationDepth: 0.42,
        metadataTags: ["tuning_flexibility", "liturgical_adaptation"]
      }),
      electric_modern_kora: variant("electric_modern_kora", {
        brightness: 0.74,
        decay: 0.52,
        attack: 0.74,
        sustain: 0.5,
        bodyGain: 0.34,
        noiseGain: 0.22,
        modulationDepth: 0.34,
        metadataTags: ["direct_pickup", "reduced_body"]
      })
    }
  },
  yidaki_didgeridoo: {
    instrumentId: "yidaki_didgeridoo",
    family: "lip_reed_drone",
    defaultVariantId: "traditional_yidaki",
    layers: {
      exciter: "Lip-reed oscillator and pressure fluctuation envelope.",
      resonator: "Bore resonator plus three moving vocal-tract formants.",
      articulation: "Mouth XY moves F1/F2/F3; tongue pulse sweeps formants and pressure.",
      residual: "Lip buzz, sideband vocalization, and breath noise."
    },
    approximationWarning:
      "Educational approximation only. Traditional Yidaki content keeps cultural protocol warning and is not an authentic recreation.",
    variantProfiles: {
      traditional_yidaki: variant("traditional_yidaki", {
        brightness: 0.42,
        decay: 0.9,
        attack: 0.5,
        sustain: 0.92,
        bodyGain: 0.82,
        noiseGain: 0.38,
        modulationDepth: 0.64,
        metadataTags: ["educational_approximation", "cultural_protocol_warning"]
      }),
      modern_didgeridoo: variant("modern_didgeridoo", {
        brightness: 0.56,
        decay: 0.88,
        attack: 0.52,
        sustain: 0.9,
        bodyGain: 0.74,
        noiseGain: 0.36,
        modulationDepth: 0.7,
        metadataTags: ["modern_context", "vocal_formants"]
      })
    }
  },
  gamelan_saron: {
    instrumentId: "gamelan_saron",
    family: "bronze_bar_modal",
    defaultVariantId: "saron_barung",
    layers: {
      exciter: "Mallet hardness controls 1-12ms strike and high partial gain.",
      resonator: "Inharmonic bronze bar modal bank with optional ombak pair.",
      articulation: "Keyup damping shortens modal decay, not only gain.",
      residual: "Mallet click and ensemble-mode beat target."
    },
    approximationWarning: "Ensemble-specific tuning approximation only. Generic slendro/pelog warnings must remain visible.",
    variantProfiles: {
      saron_barung: variant("saron_barung", {
        brightness: 0.62,
        decay: 0.44,
        attack: 0.54,
        sustain: 0.34,
        bodyGain: 0.62,
        noiseGain: 0.44,
        modulationDepth: 0.5,
        metadataTags: ["middle_register", "short_bronze_sustain", "keyup_damping"]
      }),
      saron_panerus: variant("saron_panerus", {
        brightness: 0.82,
        decay: 0.34,
        attack: 0.72,
        sustain: 0.26,
        bodyGain: 0.48,
        noiseGain: 0.5,
        modulationDepth: 0.45,
        metadataTags: ["higher_register", "bright_short_sustain", "peking"]
      })
    }
  }
};

export const advancedInstrumentIds = Object.keys(instrumentModelProfiles);

export function getInstrumentModelProfile(instrumentId: string): InstrumentModelProfile | undefined {
  return instrumentModelProfiles[instrumentId];
}

export function getVariantModelProfile(instrumentId: string, variantId?: string): VariantModelProfile | undefined {
  const profile = getInstrumentModelProfile(instrumentId);

  if (!profile) {
    return undefined;
  }

  return profile.variantProfiles[variantId ?? profile.defaultVariantId] ?? profile.variantProfiles[profile.defaultVariantId];
}

export function getAllVariantModelProfiles(): VariantModelProfile[] {
  return Object.values(instrumentModelProfiles).flatMap((profile) => Object.values(profile.variantProfiles));
}
